import {
  canonicalJson,
  sha256Hex,
} from '@trackeat/shared/hash';
import type {
  StockCommandMasterEffectV1,
  SyncEventInput,
} from '@trackeat/shared/sync';

export const STOCK_COMMAND_WRITE_PATH = 'stock_command_only' as const;

export type StockCommandStatus = 'pending' | 'sending' | 'conflict';

/**
 * Minimum identity required by the local outbox. The versioned domain command
 * may add fields without changing this adapter.
 */
export interface StockCommandIdentity {
  command_id: string;
  tenant_id: string;
}

export interface StockCommandConflict {
  code: string;
  reason: string;
  server_digest?: string | null;
  details?: unknown;
}

export interface StockCommandOutboxRecord<
  Command extends StockCommandIdentity = StockCommandIdentity,
> {
  command_id: string;
  digest: string;
  command: Command;
  master_effects: readonly StockCommandMasterEffectV1[];
  status: StockCommandStatus;
  attempts: number;
  optimistic_event_ids: readonly string[];
  last_error: string | null;
  conflict: StockCommandConflict | null;
  queued_at: string;
  updated_at: string;
}

export interface LinkedOptimisticStockEvent {
  command_id: string;
  command_digest: string;
  event_id: string;
  event: SyncEventInput;
}

export type StockCommandFinalOutcome = 'acknowledged' | 'permanently_rejected';

/**
 * Durable tombstone kept after the active envelope is removed. Without it, a
 * UI retry after ACK could enqueue the same command_id with fresh optimistic
 * UUIDs that the original server writeback can never replace.
 */
export interface StockCommandFinalization {
  command_id: string;
  digest: string;
  outcome: StockCommandFinalOutcome;
  reason: string | null;
  finalized_at: string;
}

export interface EnqueuedStockCommandHandle {
  command_id: string;
  digest: string;
  /**
   * A successful enqueue permanently chooses the command path for this
   * operation. Callers must retry/reconcile it; they must never emit the same
   * movement through the legacy event writer.
   */
  write_path: typeof STOCK_COMMAND_WRITE_PATH;
}

/**
 * Operations an adapter must expose inside ONE serializable read-write
 * transaction (for example a Dexie `db.transaction('rw', ...)`).
 *
 * The current `pending_ops` table cannot safely implement this contract: its
 * CHECK accepts only `event|master`, while `events_local` has no command link
 * or command lifecycle state. This module deliberately does not overload
 * those legacy rows. A schema-owning change can implement this interface
 * without changing the pure lifecycle below.
 */
export interface StockCommandStorageTransaction<
  Command extends StockCommandIdentity = StockCommandIdentity,
> {
  getCommand(commandId: string): Promise<StockCommandOutboxRecord<Command> | null>;
  getFinalization(commandId: string): Promise<StockCommandFinalization | null>;
  addCommand(record: StockCommandOutboxRecord<Command>): Promise<void>;
  putCommand(record: StockCommandOutboxRecord<Command>): Promise<void>;
  deleteCommand(commandId: string): Promise<void>;
  addFinalization(finalization: StockCommandFinalization): Promise<void>;
  archiveRejectedCommand?(
    record: StockCommandOutboxRecord<Command>,
    reason: string,
    rejectedAt: string,
  ): Promise<void>;
  addOptimisticEvents(events: readonly LinkedOptimisticStockEvent[]): Promise<void>;
  addOptimisticMasterEffects?(
    commandId: string,
    effects: readonly StockCommandMasterEffectV1[],
  ): Promise<void>;
  finalizeOptimisticMasterEffects?(commandId: string): Promise<void>;
  rollbackOptimisticMasterEffects?(commandId: string): Promise<void>;
  /**
   * Delete only still-optimistic rows linked to BOTH commandId and eventIds.
   * Return the number removed. Throwing from the transaction must roll back
   * every removal.
   */
  deleteOptimisticEvents(commandId: string, eventIds: readonly string[]): Promise<number>;
}

export interface AtomicStockCommandStorage<
  Command extends StockCommandIdentity = StockCommandIdentity,
> {
  transaction<Result>(
    work: (tx: StockCommandStorageTransaction<Command>) => Promise<Result>,
  ): Promise<Result>;
}

export type StockCommandOutboxErrorCode =
  | 'invalid_input'
  | 'idempotency_conflict'
  | 'command_not_found'
  | 'digest_mismatch'
  | 'invalid_transition'
  | 'permanent_rejection_required'
  | 'optimistic_event_set_incomplete';

export class StockCommandOutboxError extends Error {
  constructor(
    readonly code: StockCommandOutboxErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'StockCommandOutboxError';
  }
}

export interface StockCommandMutationOptions {
  now?: () => string;
  masterEffects?: readonly StockCommandMasterEffectV1[];
}

export interface StockCommandEnqueueResult<
  Command extends StockCommandIdentity = StockCommandIdentity,
> {
  outcome:
    | 'queued'
    | 'already_queued'
    | 'already_acknowledged'
    | 'already_rejected';
  handle: EnqueuedStockCommandHandle;
  record: StockCommandOutboxRecord<Command> | null;
}

export interface StockCommandAckResult {
  outcome: 'acked' | 'already_finalized';
  handle: EnqueuedStockCommandHandle;
}

export interface PermanentStockCommandRejection {
  permanent: true;
  reason: string;
}

function outboxError(
  code: StockCommandOutboxErrorCode,
  message: string,
): StockCommandOutboxError {
  return new StockCommandOutboxError(code, message);
}

function mutationTimestamp(options: StockCommandMutationOptions): string {
  return options.now?.() ?? new Date().toISOString();
}

function assertNonEmpty(value: string, field: string): void {
  if (value.trim().length === 0) {
    throw outboxError('invalid_input', `${field} must not be empty`);
  }
}

function assertCanonicalJsonSafe(
  value: unknown,
  path: string,
  ancestors: WeakSet<object>,
): void {
  if (
    value === null
    || typeof value === 'string'
    || typeof value === 'boolean'
  ) {
    return;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw outboxError('invalid_input', `${path} contains a non-finite number`);
    }
    const magnitude = Math.abs(value);
    // PostgreSQL jsonb e JSON.stringify usano notazioni diverse fuori da
    // questo intervallo. Il digest comprende anche i master_effects: il client
    // deve quindi applicare lo stesso dominio numerico del DB all'intero
    // oggetto firmato, non soltanto alle linee StockCommand.
    if (magnitude !== 0 && (magnitude < 1e-6 || magnitude >= 1e21)) {
      throw outboxError(
        'invalid_input',
        `${path} is outside the canonical JSON numeric range`,
      );
    }
    return;
  }
  if (typeof value !== 'object') {
    throw outboxError('invalid_input', `${path} is not JSON-compatible`);
  }
  if (ancestors.has(value)) {
    throw outboxError('invalid_input', `${path} contains a circular reference`);
  }

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        assertCanonicalJsonSafe(item, `${path}[${index}]`, ancestors);
      });
      return;
    }

    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      throw outboxError('invalid_input', `${path} must contain plain JSON objects`);
    }
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      assertCanonicalJsonSafe(item, `${path}.${key}`, ancestors);
    }
  } finally {
    ancestors.delete(value);
  }
}

function validateCommandAndEvents(
  command: StockCommandIdentity,
  optimisticEvents: readonly SyncEventInput[],
  masterEffects: readonly StockCommandMasterEffectV1[],
): readonly string[] {
  assertNonEmpty(command.command_id, 'command_id');
  assertNonEmpty(command.tenant_id, 'tenant_id');
  if (optimisticEvents.length === 0 && masterEffects.length === 0) {
    throw outboxError(
      'invalid_input',
      'a compound needs at least one optimistic event or master effect',
    );
  }

  const eventIds = new Set<string>();
  for (const event of optimisticEvents) {
    assertNonEmpty(event.client_uuid, 'event.client_uuid');
    if (event.tenant_id !== command.tenant_id) {
      throw outboxError('invalid_input', 'optimistic event tenant does not match command tenant');
    }
    if (eventIds.has(event.client_uuid)) {
      throw outboxError('invalid_input', `duplicate optimistic event ${event.client_uuid}`);
    }
    eventIds.add(event.client_uuid);
  }

  assertCanonicalJsonSafe(command, 'command', new WeakSet<object>());
  assertCanonicalJsonSafe(optimisticEvents, 'optimistic_events', new WeakSet<object>());
  assertCanonicalJsonSafe(masterEffects, 'master_effects', new WeakSet<object>());
  if (masterEffects.length > 1_000) {
    throw outboxError('invalid_input', 'a stock command supports at most 1000 master effects');
  }
  const effectKeys = new Set<string>();
  for (const effect of masterEffects) {
    if (
      effect.table !== 'foods'
      && effect.table !== 'productions'
      && effect.table !== 'production_label_print_intents_v1'
    ) {
      throw outboxError('invalid_input', `unsupported master effect table ${effect.table}`);
    }
    if (!['insert', 'update', 'archive'].includes(effect.op)) {
      throw outboxError('invalid_input', `unsupported master effect op ${effect.op}`);
    }
    if (!Number.isInteger(effect.base_version) || effect.base_version < 0) {
      throw outboxError('invalid_input', 'master effect base_version must be nonnegative');
    }
    const key = `${effect.table}:${effect.id}`;
    if (effectKeys.has(key)) {
      throw outboxError('invalid_input', `duplicate master effect ${key}`);
    }
    effectKeys.add(key);
  }
  return [...eventIds];
}

/**
 * Hash the immutable command exactly as the server receipt does. Delivery
 * metadata outside the command (including the local optimistic row wrapper)
 * must not enter the digest.
 */
export async function stockCommandDigest<
  Command extends StockCommandIdentity,
>(
  command: Command,
  masterEffects: readonly StockCommandMasterEffectV1[] = [],
): Promise<string> {
  assertNonEmpty(command.command_id, 'command_id');
  assertNonEmpty(command.tenant_id, 'tenant_id');
  assertCanonicalJsonSafe(command, 'command', new WeakSet<object>());
  assertCanonicalJsonSafe(masterEffects, 'master_effects', new WeakSet<object>());
  return sha256Hex(canonicalJson(
    masterEffects.length === 0
      ? command
      : { command, master_effects: masterEffects },
  ));
}

function commandHandle(
  record: Pick<StockCommandOutboxRecord | StockCommandFinalization, 'command_id' | 'digest'>,
): EnqueuedStockCommandHandle {
  return {
    command_id: record.command_id,
    digest: record.digest,
    write_path: STOCK_COMMAND_WRITE_PATH,
  };
}

function assertMatchingDigest(
  record: Pick<StockCommandOutboxRecord | StockCommandFinalization, 'digest'>,
  handle: EnqueuedStockCommandHandle,
): void {
  if (record.digest !== handle.digest) {
    throw outboxError(
      'digest_mismatch',
      `digest mismatch for stock command ${handle.command_id}`,
    );
  }
}

async function requireCommand<
  Command extends StockCommandIdentity,
>(
  tx: StockCommandStorageTransaction<Command>,
  handle: EnqueuedStockCommandHandle,
): Promise<StockCommandOutboxRecord<Command>> {
  const record = await tx.getCommand(handle.command_id);
  if (!record) {
    throw outboxError(
      'command_not_found',
      `stock command ${handle.command_id} is not in the command outbox`,
    );
  }
  assertMatchingDigest(record, handle);
  return record;
}

export async function enqueueStockCommand<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  command: Command,
  optimisticEvents: readonly SyncEventInput[],
  options: StockCommandMutationOptions = {},
): Promise<StockCommandEnqueueResult<Command>> {
  const masterEffects = options.masterEffects ?? [];
  const eventIds = validateCommandAndEvents(
    command,
    optimisticEvents,
    masterEffects,
  );
  const digest = await stockCommandDigest(command, masterEffects);
  const now = mutationTimestamp(options);

  return storage.transaction(async (tx) => {
    const existing = await tx.getCommand(command.command_id);
    if (existing) {
      if (existing.digest !== digest) {
        throw outboxError(
          'idempotency_conflict',
          `command_id ${command.command_id} is already queued with a different digest`,
        );
      }
      return {
        outcome: 'already_queued',
        handle: commandHandle(existing),
        record: existing,
      };
    }

    const finalized = await tx.getFinalization(command.command_id);
    if (finalized) {
      if (finalized.digest !== digest) {
        throw outboxError(
          'idempotency_conflict',
          `command_id ${command.command_id} was finalized with a different digest`,
        );
      }
      return {
        outcome: finalized.outcome === 'acknowledged'
          ? 'already_acknowledged'
          : 'already_rejected',
        handle: commandHandle(finalized),
        record: null,
      };
    }

    const record: StockCommandOutboxRecord<Command> = {
      command_id: command.command_id,
      digest,
      command,
      master_effects: [...masterEffects],
      status: 'pending',
      attempts: 0,
      optimistic_event_ids: eventIds,
      last_error: null,
      conflict: null,
      queued_at: now,
      updated_at: now,
    };
    const linkedEvents = optimisticEvents.map((event): LinkedOptimisticStockEvent => ({
      command_id: command.command_id,
      command_digest: digest,
      event_id: event.client_uuid,
      event,
    }));

    // The adapter must roll both writes back if either one fails.
    await tx.addCommand(record);
    await tx.addOptimisticEvents(linkedEvents);
    if (masterEffects.length > 0) {
      if (!tx.addOptimisticMasterEffects) {
        throw outboxError(
          'invalid_input',
          'storage adapter does not support compound master effects',
        );
      }
      await tx.addOptimisticMasterEffects(command.command_id, masterEffects);
    }

    return {
      outcome: 'queued',
      handle: commandHandle(record),
      record,
    };
  });
}

export async function beginStockCommandSend<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  handle: EnqueuedStockCommandHandle,
  options: StockCommandMutationOptions = {},
): Promise<StockCommandOutboxRecord<Command>> {
  return storage.transaction(async (tx) => {
    const current = await requireCommand(tx, handle);
    if (current.status === 'conflict') {
      throw outboxError(
        'invalid_transition',
        `stock command ${handle.command_id} is in conflict and cannot be sent automatically`,
      );
    }
    if (current.status === 'sending') {
      // Crash recovery may re-send the same idempotent command. Do not create a
      // second attempt merely by re-reading the persisted sending lease.
      return current;
    }

    const next: StockCommandOutboxRecord<Command> = {
      ...current,
      status: 'sending',
      attempts: current.attempts + 1,
      last_error: null,
      updated_at: mutationTimestamp(options),
    };
    await tx.putCommand(next);
    return next;
  });
}

export async function retryStockCommand<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  handle: EnqueuedStockCommandHandle,
  reason: string,
  options: StockCommandMutationOptions = {},
): Promise<StockCommandOutboxRecord<Command>> {
  assertNonEmpty(reason, 'retry reason');
  return storage.transaction(async (tx) => {
    const current = await requireCommand(tx, handle);
    if (current.status === 'conflict') {
      throw outboxError(
        'invalid_transition',
        `stock command ${handle.command_id} needs conflict resolution, not an automatic retry`,
      );
    }

    const next: StockCommandOutboxRecord<Command> = {
      ...current,
      status: 'pending',
      last_error: reason,
      updated_at: mutationTimestamp(options),
    };
    await tx.putCommand(next);
    return next;
  });
}

export async function markStockCommandConflict<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  handle: EnqueuedStockCommandHandle,
  conflict: StockCommandConflict,
  options: StockCommandMutationOptions = {},
): Promise<StockCommandOutboxRecord<Command>> {
  assertNonEmpty(conflict.code, 'conflict.code');
  assertNonEmpty(conflict.reason, 'conflict.reason');
  assertCanonicalJsonSafe(conflict, 'conflict', new WeakSet<object>());

  return storage.transaction(async (tx) => {
    const current = await requireCommand(tx, handle);
    const next: StockCommandOutboxRecord<Command> = {
      ...current,
      status: 'conflict',
      last_error: conflict.reason,
      conflict,
      updated_at: mutationTimestamp(options),
    };
    await tx.putCommand(next);
    return next;
  });
}

export async function acknowledgeStockCommand<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  handle: EnqueuedStockCommandHandle,
  options: StockCommandMutationOptions = {},
): Promise<StockCommandAckResult> {
  return storage.transaction(async (tx) => {
    const current = await tx.getCommand(handle.command_id);
    if (!current) {
      const finalized = await tx.getFinalization(handle.command_id);
      if (!finalized) {
        throw outboxError(
          'command_not_found',
          `stock command ${handle.command_id} has no active envelope or finalization`,
        );
      }
      assertMatchingDigest(finalized, handle);
      if (finalized.outcome !== 'acknowledged') {
        throw outboxError(
          'invalid_transition',
          `stock command ${handle.command_id} was permanently rejected`,
        );
      }
      return { outcome: 'already_finalized', handle };
    }
    assertMatchingDigest(current, handle);
    if (current.status === 'conflict') {
      throw outboxError(
        'invalid_transition',
        `stock command ${handle.command_id} is in conflict and cannot be acknowledged`,
      );
    }

    // Optimistic facts remain: pull/writeback replaces them by client_uuid with
    // the server-authored revision/hash. Ack removes only the command envelope.
    await tx.addFinalization({
      command_id: current.command_id,
      digest: current.digest,
      outcome: 'acknowledged',
      reason: null,
      finalized_at: mutationTimestamp(options),
    });
    await tx.finalizeOptimisticMasterEffects?.(current.command_id);
    await tx.deleteCommand(handle.command_id);
    return { outcome: 'acked', handle };
  });
}

export async function rejectStockCommandPermanently<
  Command extends StockCommandIdentity,
>(
  storage: AtomicStockCommandStorage<Command>,
  handle: EnqueuedStockCommandHandle,
  rejection: PermanentStockCommandRejection,
  options: StockCommandMutationOptions = {},
): Promise<void> {
  if (rejection.permanent !== true) {
    throw outboxError(
      'permanent_rejection_required',
      'optimistic stock events may only be removed after a permanent rejection',
    );
  }
  assertNonEmpty(rejection.reason, 'rejection reason');

  await storage.transaction(async (tx) => {
    const current = await tx.getCommand(handle.command_id);
    if (!current) {
      const finalized = await tx.getFinalization(handle.command_id);
      if (!finalized) {
        throw outboxError(
          'command_not_found',
          `stock command ${handle.command_id} has no active envelope or finalization`,
        );
      }
      assertMatchingDigest(finalized, handle);
      if (finalized.outcome !== 'permanently_rejected') {
        throw outboxError(
          'invalid_transition',
          `stock command ${handle.command_id} was already acknowledged`,
        );
      }
      return;
    }
    assertMatchingDigest(current, handle);
    const finalizedAt = mutationTimestamp(options);
    await tx.addFinalization({
      command_id: current.command_id,
      digest: current.digest,
      outcome: 'permanently_rejected',
      reason: rejection.reason,
      finalized_at: finalizedAt,
    });
    // The active envelope must leave the runnable queue, but its immutable
    // payload remains available for a safe operator/support reconciliation.
    // Production SQLite implements this hook in the same transaction; pure
    // adapters may omit it when they have no durable dead-letter storage.
    await tx.archiveRejectedCommand?.(
      current,
      rejection.reason,
      finalizedAt,
    );
    const expected = current.optimistic_event_ids.length;
    const removed = await tx.deleteOptimisticEvents(
      current.command_id,
      current.optimistic_event_ids,
    );
    if (removed !== expected) {
      throw outboxError(
        'optimistic_event_set_incomplete',
        `permanent rejection removed ${removed}/${expected} linked optimistic events`,
      );
    }
    if (current.master_effects.length > 0) {
      if (!tx.rollbackOptimisticMasterEffects) {
        throw outboxError(
          'invalid_transition',
          'storage adapter cannot roll back compound master effects',
        );
      }
      await tx.rollbackOptimisticMasterEffects(current.command_id);
    }

    // Delete last so a rollback preserves both envelope and optimistic facts.
    await tx.deleteCommand(current.command_id);
  });
}
